import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useLayout, usePageStrings } from "../../../common/hooks";
import { ActionStatus, AppDispatch, RootState, useAppDispatch, useAppSelector } from "../../../store";
import { appLogService } from "../../../store/slices/appLog";
import { signInWithAzure, signInWithOkta } from "../../../store/slices/authorization";
import { Divider, FilledTonalButton, Text } from "../../../common/components";
import SignInUserAndPassword from "./signInUserAndPassword/SignInUserAndPassword";
import urlService from "../../../common/services/url.service";
import { createMsalInstance, loginRequest } from "../../../authAzureConfig";
import { Auth0Client } from "@auth0/auth0-spa-js";
import { Authentication } from "../../../viewModels/application/configuration/authentication";


import styles from "./SignIn.module.scss";
import { SignInBannerMessage } from "./signInBannerMessage/SignInBannerMessage";
import OktaAuth, { TokenParams } from "@okta/okta-auth-js";
import { localStorageService } from "../../../common/services/localStorage.service";

export function SignIn() {

    const dispatch = useAppDispatch();

    const pageStrings = usePageStrings();
    const [searchParams] = useSearchParams();
    const authorization = useAppSelector(state => state.authorization);
    const configuration = useAppSelector((state: RootState) => state.configuration);

    const isUserAndPasswordEnabled = configuration.authentication.usernameAndPassword_isEnabled === true;

    const isSsoEnabled =
        configuration.authentication.adfs_IsEnabled === true ||
        configuration.authentication.azure_IsEnabled === true ||
        configuration.authentication.okta_isEnabled === true;


    // Configure layout
    useLayout({
        authorizationLayoutConfiguration: "default"
    });

    useEffect(() => {
        if (authorization.signInStatus === ActionStatus.idle) {

            if (!isUserAndPasswordEnabled && isSsoEnabled) {

                 handleSingleSignOn()
            }
        }
    }, [isUserAndPasswordEnabled, isSsoEnabled]);


    function handleSingleSignOn() {
        // Store refUrl in localStorage before SSO redirect only if it exists
        const refUrl = searchParams.get('refUrl');
        if (refUrl) {
            localStorageService.setSsoRefUrl(refUrl);
        }
        
        loginWithSSO(configuration.authentication, dispatch);
    }

    return (
        <div className={styles["SignIn"]}>
            <header>
                <h1 className={styles["SignIn-heading1"]}>
                    <Text fontStyle="headline-small" value={pageStrings.signInPage.heading_welcome}></Text>
                </h1>
            </header>

            <SignInBannerMessage></SignInBannerMessage>

            <div className={styles["SignIn-content-container"]}>
                <div className={styles["SignIn-content"]}>
                    {isUserAndPasswordEnabled && (
                        <SignInUserAndPassword></SignInUserAndPassword>
                    )}

                    {(isUserAndPasswordEnabled && isSsoEnabled) && <Divider></Divider>}

                    {isSsoEnabled && (
                        <FilledTonalButton type={"button"} value={pageStrings.signInPage.button_singleSignOn_label} onClick={handleSingleSignOn}></FilledTonalButton>
                    )}
                </div>
            </div>
        </div>
    );

}



function loginWithSSO(configurationAuthentication: Authentication, dispatch: AppDispatch) {

    if (configurationAuthentication.azure_IsEnabled) {
        loginWithAzureSSO(configurationAuthentication, dispatch);
    } else if (configurationAuthentication.okta_isEnabled) {
        loginWithOktaSSO(configurationAuthentication, dispatch);
    } else if (configurationAuthentication.adfs_IsEnabled) {
        loginWithAdfsSSO(configurationAuthentication);
    }

};

function loginWithAzureSSO(configurationAuthentication: Authentication, dispatch: AppDispatch) {
    
    createMsalInstance({
        azure_ClientId: configurationAuthentication.azure_ClientId,
        azure_TenantId: configurationAuthentication.azure_TenantId,
        azure_RedirectUri: configurationAuthentication.azure_RedirectUri,
    }).then((msalInstance) => {
        msalInstance
            .loginPopup(loginRequest)
            .then((response) => {

                dispatch(signInWithAzure({
                    token: response.accessToken
                }));

            })
            .catch(reason => {
                appLogService.fatal("SignIn.tsx", 'loginWithAzureSSO', "SignIn", JSON.stringify(reason));
            });
    });


}

function loginWithAdfsSSO(configurationAuthentication: Authentication) {

    // ADFS URL and client ID
    const adfsUrl = configurationAuthentication.adfs_OrgUri;
    const clientId = configurationAuthentication.adfs_ClientId;

    // Use the specified callback URL for redirection after login
    const redirectUri = encodeURIComponent(configurationAuthentication.adfs_ReturnUrl);

    // Construct the full ADFS URL for SSO
    const loginUrl = `${adfsUrl}?client_id=${clientId}&response_type=token&redirect_uri=${redirectUri}&resource=${clientId}`;

    // Redirect the user to ADFS for authentication
    window.location.href = loginUrl;

}

// NEW: Proper Okta SSO implementation
async function loginWithOktaSSO(configurationAuthentication: Authentication, dispatch: AppDispatch) {
    try {   
        
        let OrgUri = `https://${configurationAuthentication.okta_AccountName}`;

        if(!!configurationAuthentication.okta_AuthorizationServer){
            OrgUri = `${OrgUri}/oauth2/${configurationAuthentication.okta_AuthorizationServer}`;
        }

        console.log('Okta OrgUri:', OrgUri);

        const oktaAuth = new OktaAuth({
            issuer: OrgUri,
            clientId: configurationAuthentication.okta_AppId ?? '',
            redirectUri: configurationAuthentication.okta_ReturnUrl,
            // scopes: ['openid', 'profile', 'email'],
            responseType: ['token', 'id_token'],
            // responseType: ['token', 'id_token'],
            // pkce: false
            // headers: {
            //     "Access-Control-Allow-Origin": `https://trial-2332994.okta.com`
            // }
        });

        console.log('Okta oktaAuth:', oktaAuth);

        // // Use popup for login (returns tokens directly)
        // const tokens= await oktaAuth.token.getWithPopup({
        //     popupTitle: 'Sign in to LMS'
        // });
        const tokenParams: TokenParams = {
            scopes: ['openid', 'email'],
        };
        oktaAuth.signInWithRedirect(tokenParams);

        // console.log('Okta tokens:', tokens);

        // if (tokens.tokens && tokens.tokens.idToken) {
        //     dispatch(signInWithOkta({
        //         token: tokens.tokens.idToken.idToken
        //     }));
        // } else if (tokens.tokens && tokens.tokens.accessToken) {
        //     dispatch(signInWithOkta({
        //         token: tokens.tokens.accessToken.accessToken
        //     }));
        // }


    } catch (error: any) {
        console.error('Error in loginWithOktaSSO:', error);
        appLogService.fatal("SignIn.tsx", 'loginWithOktaSSO', "Exception", error.message);
    }
}

// RENAMED: Your current implementation renamed to Auth0
// async function loginWithAuth0SSO(configurationAuthentication: Authentication, dispatch: AppDispatch) {
//     try {
//         console.log(configurationAuthentication);
//         const auth0 = new Auth0Client({
//             domain: configurationAuthentication.auth0_Domain ?? "", // Rename from okta_AccountName
//             clientId: configurationAuthentication.auth0_ClientId ?? "", // Rename from okta_AppId
//             authorizationParams: {
//                 redirect_uri: configurationAuthentication.auth0_ReturnUrl, // Rename from okta_ReturnUrl
//                 audience: configurationAuthentication.auth0_Audience ?? null, // Rename from okta_AuthorizationServer
//                 responseType: "token id_token",
//                 scope: "openid profile email",
//             },
//         });

//         await auth0.loginWithPopup().then(() => {
//             auth0.getIdTokenClaims().then((idToken) => {
//                 if (idToken) {
//                     dispatch(signInWithAuth0({ // Create new action
//                         token: idToken.__raw
//                     }));
//                 }
//             });
//         });
//     } catch (error: any) {
//         appLogService.fatal("SignIn.tsx", 'loginWithAuth0SSO', "Exception", error.message);
//}